import React, { useState, useEffect } from "react";
import { Box, FormControl, FormLabel, Select, Input, Button, useToast, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, Text, Table, Thead, Tbody, Tr, Th, Td, Badge, Checkbox, Tooltip, HStack, Tabs, TabList, TabPanels, Tab, TabPanel } from "@chakra-ui/react";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import BulkSalesUpload from "./BulkSalesUpload";
import ClicksUpload from "./ClicksUpload";
import BulkStrikeRateUpload from "./BulkStrikeRateUpload";
import OrdersUpload from "./OrdersUpload";
import { isMasterUser, isOrgNewDataUser, fetchSummary } from "../../../components/newData/useNewData";
import NewData from "./NewData";

const Imports = () => {
  const { selectedRetailer, selectedSupplier, selectedOrg } = useSupplierRetailer();
  const [dataType, setDataType] = useState("");
  const [importOption, setImportOption] = useState("");
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // New Data tab (masters only) — badge + tab refetch after every successful
  // upload (ndRefresh), so admins never have to reload the app to see new items.
  const isMaster = typeof isMasterUser === "function" ? isMasterUser() : !!isMasterUser;
  // Blue Mango's org admin (2026-10-02): its own scoped New Data tab, and uploads
  // only for the org's own suppliers (the summary carries that list).
  const isOrgUser = isOrgNewDataUser();
  const hasNewData = isMaster || isOrgUser;
  const [ownSuppliers, setOwnSuppliers] = useState(null);
  const [newDataPending, setNewDataPending] = useState(0);
  const [ndRefresh, setNdRefresh] = useState(0);
  const onImported = () => {
    setNdRefresh((k) => k + 1);
    // a fresh import with new items should be able to re-show the sign-in popup
    try { sessionStorage.removeItem("spearbi_newdata_dismissed"); } catch (e) {}
  };
  useEffect(() => {
    if (!hasNewData) return;
    let cancelled = false;
    fetchSummary()
      .then((s) => {
        if (cancelled || !s) return;
        if (isOrgUser) setOwnSuppliers((s.suppliers || []).map((x) => String(x).toUpperCase()));
        if (!s.pending) return;
        const held = isOrgUser ? Number(s.holds?.rows) || 0 : 0;
        setNewDataPending((Number(s.pending.store) || 0) + (Number(s.pending.article) || 0) + held);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [hasNewData, isOrgUser, ndRefresh]);

  const [imports, setImports] = useState({
    // Wording 2026-10-02 (Helena): "SOH", never "SDC", and say what each file brings in.
    // Only the labels changed — every value (and so every backend route) is the same.
    PNP: [
      { value: "sales", text: "Sales" },
      { value: "sdc", text: "SOH & Ranging" },
      { value: "articles", text: "Articles" },
      { value: "stores", text: "Stores" },
      { value: "strikerate", text: "Strike Rate" },
      // PnP purchase-order file (PnP_Orders_*.csv) -> the Orders page ledger. Masters only —
      // filtered out below for everyone else. docs/michael/pnp-orders/TASK-orders.md
      { value: "orders", text: "Orders (PnP PO file)" },
    ],
    CHK: [
      // The weekly Separate View carries sales, stock and ranging — one upload imports all
      // three (chkImportV2.manualSdc, 2026-10-02). "Sales" = the bulk monthly sales files.
      { value: "sdc", text: "Sales, SOH & Ranging (weekly file)" },
      { value: "sales", text: "Sales (monthly files)" },
      { value: "dc", text: "DC stock" },
      { value: "articles", text: "Articles" },
      { value: "stores", text: "Stores" },
    ],
    DIS: [
      { value: "sales", text: "Sales" },
      // Store stock + DC: multi-file validate-then-commit (BulkSalesUpload dataType=sdc), rows
      // attributed per article owner, unknown articles held — docs/michael/dis-imports/TASK-dis-multi-supplier-import.md
      { value: "sdc", text: "SOH + DC (stock file)" },//sdc units
      { value: "ranging", text: "Ranging" },//site_article_status
      { value: "articles", text: "Articles" },
      { value: "stores", text: "Stores" },
    ],
    // CLI 2026-09: the crawler is dormant — the new Clicks portal export carries
    // sales AND store stock in ONE monthly file, so the old Sales/SDC options are
    // replaced by "sales-soh" (multi-file, validate-then-commit) and "dc" now
    // routes to the same new uploader. Legacy parsers stay in the backend for the
    // crawler. docs/michael/clicks/TASK-clicks-import.md
    CLI: [
      // One monthly file = sales + store stock + ranging (SKU_STATUS A/N/I, 2026-10-01), so the
      // separate legacy "Ranging" upload is gone from the menu (a sales file sent through it wrote
      // dates as store codes). import_ranging_cli stays in the backend.
      { value: "sales-soh", text: "Sales, SOH & Ranging (monthly file)" },
      { value: "dc", text: "DC stock" },
      { value: "articles", text: "Articles" },
      { value: "Stores", text: "Stores" },
    ],
    MAK: [
      { value: "sales", text: "Sales" },
      { value: "sdc", text: "SOH" },
      { value: "articles", text: "Articles" },
      { value: "stores", text: "Stores" },
    ],
  });
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (selectedRetailer) {
      // Orders + Stores are the two masters' only (stores: Michael, 2026-10-02 — the API refuses it too)
      setOptions((imports[selectedRetailer] || []).filter((o) => !["orders", "stores"].includes(o.value) || isMaster));
    }
  }, [selectedRetailer, imports, isMaster]);

  const handleSubmit = async () => {
    onOpen();
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    setFileName(event.target.files[0]?.name || "");
  };

  const confirmSubmit = async () => {
    onClose();
    setIsLoading(true);

    if (!file) {
      toast({
        title: "Error",
        description: "Please select a file",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
      return;
    }

    let supplier = selectedSupplier; // Replace with your actual supplier value
    const vars = {
      supplier: supplier.toLowerCase(),
      retailer: selectedRetailer.toLowerCase(),
      option:importOption.toLowerCase()
    };

    const formData = new FormData();
    formData.append("xlsxFile", file);
    formData.append("org_code", selectedOrg || ""); // server refuses another organisation's suppliers (2026-10-06)
    Object.keys(vars).forEach((key) => {
      formData.append(key, vars[key]);
    });
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/data/uploads/`, {
        method: "POST",
        body: formData,
      });
      const status = response.status;
      const data = await response.json();
      console.log({ data });
      if (status === 201) {
        toast({
          title: "Success",
          description: data.message,
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        if (hasNewData) onImported();
      } else {
        toast({
          title: "Error",
          description: data.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error during file upload:", error);
      toast({
        title: "Error",
        description: `Exception occurred during file upload: ${error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  const isBulkSales = importOption.toLowerCase() === "sales" && ["PNP", "CHK", "DIS"].includes(selectedRetailer);
  // Dischem store stock + DC (2026-09-17): the same multi-file screen, dataType=sdc
  const isDisStock = selectedRetailer === "DIS" && importOption.toLowerCase() === "sdc";
  // PnP strike rate: multi-file validate-then-commit, rows attributed per vendor number (POST /data/bulk-strikerate)
  const isBulkStrikeRate = selectedRetailer === "PNP" && importOption.toLowerCase() === "strikerate";
  // Clicks portal exports go through their own multi-file validate-then-commit
  // path (POST /data/clicks-upload) — CLI's "dc" option included, unlike CHK's.
  const isClicksUpload = selectedRetailer === "CLI" && ["sales-soh", "dc"].includes(importOption.toLowerCase());
  // PnP orders file: single-file validate-then-commit into the Orders ledger (POST /orders/upload, masters only)
  const isOrdersUpload = selectedRetailer === "PNP" && importOption.toLowerCase() === "orders" && isMaster;

  // A supplier an org admin can see but does not administer (Blue Mango's Spear guests).
  const notOwnSupplier = isOrgUser && ownSuppliers && selectedSupplier && !ownSuppliers.includes(String(selectedSupplier).toUpperCase());
  const notice = (
    <Box maxW="500px" mx="auto" mt={8} p={4} borderWidth="1px" borderRadius="lg">
      <Text>Imports are not available for {selectedSupplier} — this supplier is managed by Spear.</Text>
    </Box>
  );

  const uploadForm = notOwnSupplier ? notice : (
    <Box maxW={isBulkSales || isDisStock || isClicksUpload || isBulkStrikeRate || isOrdersUpload ? "1000px" : "500px"} mx="auto" mt={8} p={4} borderWidth="1px" borderRadius="lg">
      <FormControl mb={4}>
        <FormLabel>Selected Retailer: {selectedRetailer}</FormLabel>
      </FormControl>
      <FormControl mb={4} isRequired>
        <FormLabel>Data Type</FormLabel>
        <Select placeholder="Select data type" value={importOption} onChange={(e) => setImportOption(e.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </Select>
      </FormControl>

      {isOrdersUpload ? (
        <OrdersUpload supplier={selectedSupplier} />
      ) : isClicksUpload ? (
        <ClicksUpload supplier={selectedSupplier} dataType={importOption.toLowerCase()} onImported={hasNewData ? onImported : undefined} />
      ) : isBulkSales || isDisStock ? (
        <BulkSalesUpload retailer={selectedRetailer} supplier={selectedSupplier} dataType={isDisStock ? "sdc" : "sales"} onImported={hasNewData ? onImported : undefined} />
      ) : isBulkStrikeRate ? (
        <BulkStrikeRateUpload supplier={selectedSupplier} onImported={hasNewData ? onImported : undefined} />
      ) : (
        <>
      <FormControl mb={4} isRequired>
        <FormLabel>Upload File</FormLabel>
        <Input type="file" accept=".xlsx" onChange={handleFileChange} />
      </FormControl>
      <Button colorScheme="blue" onClick={handleSubmit} isLoading={isLoading}>
        Submit
      </Button>
        </>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Upload</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              <strong>Retailer:</strong> {selectedRetailer}
            </Text>
            <Text>
              <strong>Data Type:</strong> {importOption}
            </Text>
            <Text>
              <strong>File:</strong> {fileName}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={confirmSubmit}>
              Confirm
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );

  if (!hasNewData) {
    return uploadForm;
  }

  return (
    <Tabs isLazy lazyBehavior="keepMounted" variant="line">
      <TabList>
        <Tab>Upload</Tab>
        <Tab>
          New Data
          {newDataPending > 0 && (
            <Badge ml={2} borderRadius="full" colorScheme="orange">
              {newDataPending}
            </Badge>
          )}
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel px={0}>{uploadForm}</TabPanel>
        <TabPanel px={0}>
          <NewData refreshKey={ndRefresh} scoped={isOrgUser} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default Imports;
